import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Film, Sparkles, Loader2, ExternalLink, Plug, RefreshCw, AlertTriangle, Play, Layers3, Share2,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { api, API_BASE } from "@/lib/api";
import CommercialPublishModal from "@/components/commercials/CommercialPublishModal";

/**
 * CommercialAvatarPanel
 *
 * Inline "AI Avatar Studio" experience embedded on the Commercial detail
 * page.  Users kept getting stuck bouncing to the standalone
 * `/dashboard/avatar-studio` route and losing the commercial's context
 * ("AI Avatar Studio won't let me create the right commercial").  This
 * component lets producers pick an avatar and hit Render *without ever
 * leaving the commercial workflow*.
 *
 * Only the Synthesia render path is exposed here — that's the most
 * complete backend flow and covers 95% of ad-video needs.  Producers who
 * need HeyGen / D-ID / translation can still open the full studio via
 * the "Open full studio →" link.
 *
 * Renders queued from here are tagged with the commercial's advertiser
 * so we can filter the "Recent avatar renders" list to just this ad.
 *
 * Props:
 *   commercial       — the full commercial doc { commercial_id, advertiser, duration_sec, chosen_script_id, scripts }
 *   chosenScriptText — the CURRENT editable script text (parent owns editing state)
 *   defaultTitle     — string to preseed the video title field
 */
export default function CommercialAvatarPanel({ commercial, chosenScriptText, defaultTitle }) {
  const [status, setStatus] = useState(null);         // /avatar-studio/status
  const [statusLoading, setStatusLoading] = useState(true);
  const [avatars, setAvatars] = useState(null);       // null = not yet loaded
  const [voices, setVoices] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [connecting, setConnecting] = useState(false);

  const [form, setForm] = useState({
    title: defaultTitle || "",
    avatar_id: "",
    voice_id: "",
    background: "off_white",
  });
  const [rendering, setRendering] = useState(false);
  const [videos, setVideos] = useState([]);
  const [pollingIds, setPollingIds] = useState(() => new Set());

  // Update #191 — station-uploaded custom avatars (My Avatars) + D-ID
  // connection state. Custom photo avatars render through D-ID's
  // photo-animation API instead of Synthesia.
  const [customAvatars, setCustomAvatars] = useState([]);
  const [didConnected, setDidConnected] = useState(false);

  // Update #164 — track fuse-with-bed jobs per avatar video so the
  // button can show "Fusing…" / "Ready" states inline.
  const [fuseJobs, setFuseJobs] = useState({});   // video_id → { status, job_id, ready_url }

  // Update #165 — social/WP publish modal for the fused TV commercial.
  const [publishOpen, setPublishOpen] = useState(false);

  // Sync the title field if the parent's default changes (e.g. user picks
  // a different variation).  We never overwrite a title the user has
  // manually typed.
  useEffect(() => {
    setForm((f) => (f.title ? f : { ...f, title: defaultTitle || f.title }));
  }, [defaultTitle]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get("/avatar-studio/status");
      setStatus(r.data);
    } catch (e) {
      // 402 = plan-gated. Anything else means the endpoint is down; treat
      // as disconnected so the user still sees a useful state.
      if (e?.response?.status === 402) {
        setStatus({ connected: false, plan_gated: true });
      } else {
        setStatus({ connected: false });
      }
    } finally { setStatusLoading(false); }
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [a, v] = await Promise.all([
        api.get("/avatar-studio/avatars"),
        api.get("/avatar-studio/voices"),
      ]);
      setAvatars(a.data.items || []);
      setVoices(v.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Synthesia catalog");
      setAvatars([]); setVoices([]);
    } finally { setCatalogLoading(false); }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const r = await api.get("/avatar-studio/renders");
      const items = r.data?.items || [];
      // Filter to just videos linked to this commercial (title-tagged
      // during render — see `renderVideo` below).
      const tag = `[${commercial.advertiser}]`;
      setVideos(items.filter((it) => (it.title || "").includes(tag)));
    } catch { /* silent — panel just shows empty list */ }
  }, [commercial.advertiser]);

  useEffect(() => { loadStatus(); loadVideos(); }, [loadStatus, loadVideos]);
  useEffect(() => {
    if (status?.connected && avatars === null) loadCatalog();
  }, [status?.connected, avatars, loadCatalog]);

  // Update #191 — load custom avatars + D-ID connection state once.
  useEffect(() => {
    api.get("/news-studio/avatars")
      .then((r) => {
        const items = r.data?.items || [];
        setCustomAvatars(items.filter(
          (a) => a.sub_method === "still_photo" && ["ready", "photo_ready"].includes(a.status),
        ));
      })
      .catch(() => {});
    api.get("/avatar-studio/providers")
      .then((r) => {
        const did = (r.data?.items || []).find((p) => p.provider === "did");
        setDidConnected(!!did?.connected);
      })
      .catch(() => {});
  }, []);

  // Update #164b — hydrate `fuseJobs` from the persisted commercial doc so
  // the fused-video player reappears on hard refresh / navigation-back.
  // Without this, the fuse UI resets to "not fused" every reload even
  // though a fused MP4 already lives in storage.
  useEffect(() => {
    const vid = commercial?.fused_video_video_id;
    if (!vid || !commercial?.fused_video_storage_path) return;
    setFuseJobs((prev) => {
      // Don't clobber an in-flight local job for the same video.
      if (prev[vid] && (prev[vid].status === "queued" || prev[vid].status === "processing")) {
        return prev;
      }
      return {
        ...prev,
        [vid]: {
          status: "done",
          ready_url: `${API_BASE}/commercials/${commercial.commercial_id}/fused-video/${vid}`,
          bed_included: true,
        },
      };
    });
  }, [commercial?.fused_video_video_id, commercial?.fused_video_storage_path, commercial?.commercial_id]);

  const connect = async (e) => {
    e?.preventDefault?.();
    if (!keyInput.trim()) return;
    setConnecting(true);
    try {
      const r = await api.post("/avatar-studio/connect", { api_key: keyInput.trim() });
      if (r.data?.warning) toast.warning(r.data.warning, { duration: 8000 });
      else toast.success("Synthesia connected");
      setKeyInput("");
      setShowConnect(false);
      loadStatus();
      loadCatalog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not connect — double-check the key");
    } finally { setConnecting(false); }
  };

  const isCustomPick = form.avatar_id.startsWith("custom:");

  const renderVideo = async () => {
    const script = (chosenScriptText || "").trim();
    if (!script) { toast.error("Pick a script variation in Step 1 first"); return; }
    if (!form.avatar_id) { toast.error("Pick an avatar"); return; }
    if (isCustomPick && !didConnected) {
      toast.error("Connect D-ID in the full Avatar Studio to animate your own avatars");
      return;
    }
    setRendering(true);
    try {
      // Tag the title with `[<advertiser>]` so `loadVideos` can filter to
      // just this commercial's renders.
      const titleTag = `[${commercial.advertiser}]`;
      const titleRoot = (form.title || "Commercial").replace(titleTag, "").trim();
      const taggedTitle = `${titleRoot} ${titleTag}`.trim();
      if (isCustomPick) {
        await api.post("/avatar-studio/render-custom", {
          custom_avatar_id: form.avatar_id.slice(7),
          title: taggedTitle,
          script,
        });
        toast.success("Rendering with D-ID — your avatar will be talking in ~1–2 min");
      } else {
        await api.post("/avatar-studio/videos", {
          title: taggedTitle,
          script,
          avatar_id: form.avatar_id,
          voice_id: form.voice_id || undefined,
          background: form.background || undefined,
          test: true,
        });
        toast.success("Rendering — Synthesia will finish in ~1–3 min");
      }
      loadVideos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed — check credits or the script length");
    } finally { setRendering(false); }
  };

  // Poll the "queued" and "in_progress" videos every 12 s so producers
  // see the state flip to "complete" without needing a page refresh.
  useEffect(() => {
    const inflight = videos.filter((v) => v.status === "queued" || v.status === "in_progress");
    if (inflight.length === 0) return;
    const t = setTimeout(async () => {
      for (const v of inflight) {
        setPollingIds((prev) => new Set(prev).add(v.video_id));
        try {
          // Provider-aware refresh — D-ID / HeyGen rows poll through the
          // multi-provider route; Synthesia keeps its legacy route.
          const url = v.provider && v.provider !== "synthesia"
            ? `/avatar-studio/providers/${v.provider}/videos/${v.video_id}`
            : `/avatar-studio/videos/${v.video_id}`;
          const r = await api.get(url);
          setVideos((prev) => prev.map((x) => (x.video_id === v.video_id ? { ...x, ...r.data } : x)));
        } catch { /* silent */ }
        setPollingIds((prev) => {
          const next = new Set(prev);
          next.delete(v.video_id);
          return next;
        });
      }
    }, 12000);
    return () => clearTimeout(t);
  }, [videos]);

  // Update #164 — kick off a background job to fuse the avatar MP4
  // with the commercial's audio bed (from Step 3). Persists per-video
  // job state locally + polls until the FFmpeg pipeline finishes.
  const startFuse = useCallback(async (videoId) => {
    setFuseJobs((prev) => ({
      ...prev,
      [videoId]: { ...(prev[videoId] || {}), status: "queued" },
    }));
    try {
      const r = await api.post(`/commercials/${commercial.commercial_id}/fuse-avatar/${videoId}`);
      setFuseJobs((prev) => ({
        ...prev,
        [videoId]: {
          status: r.data.status,
          job_id: r.data.job_id,
          bed_included: r.data.bed_included,
        },
      }));
      toast.success("Fusing avatar + audio bed — usually finishes in under a minute");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Fuse failed to start";
      toast.error(detail);
      setFuseJobs((prev) => {
        const next = { ...prev };
        delete next[videoId];
        return next;
      });
    }
  }, [commercial.commercial_id]);

  // Poll any in-flight fuse jobs every 5 s.
  useEffect(() => {
    const inflight = Object.entries(fuseJobs).filter(
      ([, j]) => j.status === "queued" || j.status === "processing",
    );
    if (inflight.length === 0) return;
    const t = setTimeout(async () => {
      for (const [videoId, job] of inflight) {
        if (!job.job_id) continue;
        try {
          const r = await api.get(`/commercials/fuse-jobs/${job.job_id}`);
          setFuseJobs((prev) => ({
            ...prev,
            [videoId]: {
              ...prev[videoId],
              ...r.data,
              ready_url: r.data.status === "done"
                ? `${API_BASE}/commercials/${commercial.commercial_id}/fused-video/${videoId}`
                : prev[videoId]?.ready_url,
            },
          }));
          if (r.data.status === "done") {
            toast.success("Fused TV commercial ready — playing below");
          } else if (r.data.status === "failed") {
            toast.error(`Fuse failed: ${r.data.error || "check the job log"}`);
          }
        } catch { /* silent */ }
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [fuseJobs, commercial.commercial_id]);

  const canRender = useMemo(
    () => !!(chosenScriptText && chosenScriptText.trim() && form.avatar_id
             && (isCustomPick ? didConnected : status?.connected)),
    [chosenScriptText, form.avatar_id, status?.connected, isCustomPick, didConnected],
  );

  // ── Not connected — show a compact onboarding block ────────────
  if (statusLoading) {
    return (
      <div className="rounded-md border border-dashed border-line bg-bg-elevated px-4 py-6 text-center text-xs text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
        Checking Avatar Studio status…
      </div>
    );
  }

  if (!status?.connected && !didConnected) {
    return (
      <div className="rounded-md border border-dashed border-brand-amber/40 bg-brand-amber/5 p-4" data-testid="commercial-avatar-connect-block">
        <div className="flex items-start gap-3">
          <Film className="h-5 w-5 text-brand-amber shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-sm font-bold text-ink-primary">Connect Synthesia to render this commercial as a talking-avatar video</div>
            <p className="text-xs text-ink-muted mt-1 leading-relaxed">
              Get a free Synthesia API key from{" "}
              <a href="https://www.synthesia.io/api" target="_blank" rel="noreferrer" className="text-brand-amber underline">
                synthesia.io/api
              </a>
              . One-off connection — you'll then be able to render straight from this page.
              {" "}Prefer HeyGen or D-ID?{" "}
              <Link to="/dashboard/avatar-studio" className="text-brand-amber underline">
                Open the full studio →
              </Link>
            </p>
            {!showConnect ? (
              <button
                type="button"
                onClick={() => setShowConnect(true)}
                className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-amber px-3 py-1.5 text-xs font-semibold text-black amber-glow"
                data-testid="commercial-avatar-connect-cta"
              >
                <Plug className="h-3 w-3" />
                Connect Synthesia
              </button>
            ) : (
              <form onSubmit={connect} className="mt-3 flex gap-2" data-testid="commercial-avatar-connect-form">
                <input
                  type="password"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="Paste your Synthesia API key"
                  className="flex-1 rounded-md border border-line bg-bg px-2 py-1.5 text-xs text-ink-primary"
                  autoFocus
                  data-testid="commercial-avatar-connect-input"
                />
                <button
                  type="submit"
                  disabled={connecting || !keyInput.trim()}
                  className="rounded-md bg-brand-amber px-3 py-1.5 text-xs font-semibold text-black amber-glow disabled:opacity-50"
                  data-testid="commercial-avatar-connect-submit"
                >
                  {connecting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Connect"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowConnect(false); setKeyInput(""); }}
                  className="rounded-md border border-line bg-bg px-3 py-1.5 text-xs text-ink-muted hover:border-line"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Connected — full inline render form ─────────────────────────
  return (
    <div className="space-y-3" data-testid="commercial-avatar-panel">
      <div className="flex items-center justify-between text-[10px] font-mono text-ink-muted">
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
          {status?.connected
            ? <>Synthesia connected · {status?.plan_label || "Test renders free"}{didConnected ? " · D-ID connected" : ""}</>
            : "D-ID connected · your custom avatars are renderable"}
        </span>
        <Link to="/dashboard/avatar-studio" className="text-brand-amber hover:underline inline-flex items-center gap-1">
          Full studio <ExternalLink className="h-2.5 w-2.5" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Video title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs text-ink-primary"
            placeholder="Auto-tagged with the advertiser"
            data-testid="commercial-avatar-title"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Background</label>
          <select
            value={form.background}
            onChange={(e) => setForm({ ...form, background: e.target.value })}
            className="mt-1 w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs text-ink-primary"
            data-testid="commercial-avatar-background"
          >
            <option value="off_white">Off-white</option>
            <option value="green_screen">Green screen (for keying)</option>
            <option value="warm_white">Warm white</option>
            <option value="black">Black</option>
            <option value="lavender">Lavender</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted flex items-center justify-between">
            <span>Avatar</span>
            {catalogLoading && <Loader2 className="h-3 w-3 animate-spin" />}
          </label>
          <select
            value={form.avatar_id}
            onChange={(e) => setForm({ ...form, avatar_id: e.target.value })}
            className="mt-1 w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs text-ink-primary"
            data-testid="commercial-avatar-picker"
          >
            <option value="">— Pick an avatar —</option>
            {customAvatars.length > 0 && (
              <optgroup label="My avatars (your uploads)">
                {customAvatars.map((a) => (
                  <option key={a.avatar_id} value={`custom:${a.avatar_id}`}>
                    {a.name} · custom{!didConnected ? " (connect D-ID)" : ""}
                  </option>
                ))}
              </optgroup>
            )}
            {(avatars || []).length > 0 && (
              <optgroup label="Synthesia stock avatars">
                {(avatars || []).map((a) => (
                  <option key={a.avatar_id || a.id} value={a.avatar_id || a.id}>
                    {a.name || a.display_name || a.avatar_id}
                    {a.gender ? ` · ${a.gender}` : ""}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Voice (optional)</label>
          {isCustomPick ? (
            <select
              disabled
              className="mt-1 w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs text-ink-muted"
              data-testid="commercial-avatar-voice"
            >
              <option>D-ID narrator (default voice)</option>
            </select>
          ) : (
          <select
            value={form.voice_id}
            onChange={(e) => setForm({ ...form, voice_id: e.target.value })}
            className="mt-1 w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs text-ink-primary"
            data-testid="commercial-avatar-voice"
          >
            <option value="">Use avatar's default voice</option>
            {(voices || []).slice(0, 40).map((v) => (
              <option key={v.voice_id || v.id} value={v.voice_id || v.id}>
                {v.name || v.display_name} {v.language ? `· ${v.language}` : ""}
              </option>
            ))}
          </select>
          )}
        </div>
      </div>

      <div className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-[11px] text-ink-muted flex items-start gap-2">
        <Sparkles className="h-3 w-3 text-brand-amber shrink-0 mt-0.5" />
        <div>
          Uses your currently chosen script variation ({(chosenScriptText || "").trim().split(/\s+/).filter(Boolean).length} words).
          Change the pick in Step 1 or edit the text above to render a different take.
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={renderVideo}
          disabled={!canRender || rendering}
          data-testid="commercial-avatar-render"
          className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {rendering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
          Render Avatar Video
        </button>
        {!canRender && !rendering && (
          <span className="text-[10px] text-ink-muted inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            {!chosenScriptText
              ? "Pick a script variation first"
              : isCustomPick && !didConnected
              ? "Connect D-ID (full studio) to render your own avatars"
              : "Pick an avatar to enable render"}
          </span>
        )}
      </div>

      {/* Recent renders for THIS commercial */}
      {videos.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">Recent renders for this commercial</div>
          <div className="space-y-3" data-testid="commercial-avatar-videos">
            {videos.map((v) => {
              const isPolling = pollingIds.has(v.video_id);
              // Real Synthesia rows store the signed S3 URL under
              // `download_url_cached` (see avatar_studio router). Falling
              // back to that here so the inline player + fuse block appear
              // as soon as the render flips to `complete`.
              const videoSrc = v.download_url || v.stream_url || v.download_url_cached;
              const isReady = v.status === "complete" && !!videoSrc;
              const isFailed = v.status === "failed";
              const isRendering = v.status === "queued" || v.status === "in_progress";
              return (
                <div
                  key={v.video_id}
                  className="rounded-md border border-line bg-bg-elevated overflow-hidden"
                  data-testid={`commercial-avatar-video-${v.video_id}`}
                >
                  {/* Header row — title + status + refresh */}
                  <div className="flex items-center gap-3 p-3">
                    <div className="rounded-md bg-brand-amber/10 p-2 shrink-0">
                      <Film className="h-4 w-4 text-brand-amber" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate text-ink-primary">{v.title || v.video_id}</div>
                      <div className="text-[10px] text-ink-muted flex items-center gap-2 mt-0.5">
                        <StatusBadge status={v.status} />
                        {v.duration_sec ? <span>· {Math.round(v.duration_sec)}s</span> : null}
                        {isPolling && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={loadVideos}
                      className="rounded-md border border-line bg-bg p-1 text-ink-muted hover:text-brand-amber"
                      title="Refresh status now"
                      data-testid={`commercial-avatar-video-refresh-${v.video_id}`}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Rendering banner — sets expectations while Synthesia works */}
                  {isRendering && (
                    <div className="mx-3 mb-3 rounded-md border border-brand-amber/30 bg-brand-amber/5 px-3 py-2 text-[11px] text-brand-amber/90 flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Synthesia is rendering — usually finishes in <strong>1–3 minutes</strong>. This card will auto-update; no need to refresh the page.
                    </div>
                  )}

                  {/* Failure banner */}
                  {isFailed && (
                    <div className="mx-3 mb-3 rounded-md border border-brand-onair/30 bg-brand-onair/5 px-3 py-2 text-[11px] text-brand-onair flex items-start gap-2">
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold">Render failed</div>
                        <div className="text-ink-muted mt-0.5">{v.error_message || "Synthesia rejected the render — usually a script-length or credit issue. Try shortening the script or check your Synthesia dashboard."}</div>
                      </div>
                    </div>
                  )}

                  {/* Inline video player — appears the moment render completes */}
                  {isReady && (
                    <div className="border-t border-line bg-black">
                      <video
                        src={videoSrc}
                        controls
                        preload="metadata"
                        poster={v.thumbnail_url || undefined}
                        className="w-full aspect-video bg-black"
                        data-testid={`commercial-avatar-video-player-${v.video_id}`}
                      />
                      <div className="flex items-center justify-between gap-2 p-2 bg-bg-elevated">
                        <span className="text-[10px] text-ink-muted">
                          Ready to publish · {v.duration_sec ? `${Math.round(v.duration_sec)}s` : ""}
                        </span>
                        <div className="flex items-center gap-2">
                          <a
                            href={videoSrc}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-line bg-bg px-2 py-1 text-[10px] text-ink-secondary hover:text-brand-amber hover:border-brand-amber/40"
                            data-testid={`commercial-avatar-video-download-${v.video_id}`}
                          >
                            <ExternalLink className="h-3 w-3" /> Open in new tab
                          </a>
                          <a
                            href={videoSrc}
                            download
                            className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-2 py-1 text-[10px] font-semibold text-black amber-glow"
                            data-testid={`commercial-avatar-video-save-${v.video_id}`}
                          >
                            <Play className="h-3 w-3" /> Save MP4
                          </a>
                        </div>
                      </div>

                      {/* Update #164 — Fuse-with-bed CTA + inline TV player.
                          Only shown if a Step 3 imaging_project exists. */}
                      {commercial.imaging_project_id && (() => {
                        const job = fuseJobs[v.video_id];
                        const isFusing = job && (job.status === "queued" || job.status === "processing");
                        const isFused = job && job.status === "done" && job.ready_url;
                        const isFuseFailed = job && job.status === "failed";
                        return (
                          <div className="border-t border-line bg-bg-surface" data-testid={`commercial-fuse-block-${v.video_id}`}>
                            {!isFused && (
                              <div className="p-3 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 text-[11px]">
                                  <div className="rounded-md bg-brand-amber/10 p-1.5">
                                    <Layers3 className="h-3.5 w-3.5 text-brand-amber" />
                                  </div>
                                  <div>
                                    <div className="text-ink-primary font-medium">Broadcast-ready TV commercial</div>
                                    <div className="text-ink-muted">Fuse this avatar video with your Step 3 audio bed → single MP4 you can push straight to socials / TV.</div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => startFuse(v.video_id)}
                                  disabled={isFusing}
                                  data-testid={`commercial-fuse-btn-${v.video_id}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 text-[11px] font-semibold text-black amber-glow disabled:opacity-60 whitespace-nowrap"
                                >
                                  {isFusing ? (
                                    <>
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                      {job.status === "processing" ? "Fusing…" : "Queued…"}
                                    </>
                                  ) : (
                                    <>
                                      <Layers3 className="h-3 w-3" />
                                      Add audio bed
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                            {isFuseFailed && (
                              <div className="mx-3 mb-3 rounded-md border border-brand-onair/30 bg-brand-onair/5 px-3 py-2 text-[11px] text-brand-onair flex items-start gap-2">
                                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                                <div>
                                  <div className="font-semibold">Fuse failed</div>
                                  <div className="text-ink-muted mt-0.5">{job.error || "FFmpeg couldn't combine the tracks. Retry?"}</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => startFuse(v.video_id)}
                                  className="ml-auto rounded-md border border-brand-onair/40 bg-bg px-2 py-1 text-[10px] hover:border-brand-onair"
                                >
                                  Retry
                                </button>
                              </div>
                            )}
                            {isFused && (
                              <div className="bg-black">
                                <div className="px-3 py-1.5 flex items-center gap-2 text-[10px] font-mono text-brand-amber bg-bg-elevated border-b border-line">
                                  <Sparkles className="h-3 w-3" />
                                  Broadcast-ready TV commercial · voice + bed fused
                                </div>
                                <video
                                  src={job.ready_url}
                                  controls
                                  preload="metadata"
                                  className="w-full aspect-video bg-black"
                                  data-testid={`commercial-fused-player-${v.video_id}`}
                                />
                                <div className="flex items-center justify-between gap-2 p-2 bg-bg-elevated">
                                  <span className="text-[10px] text-ink-muted">
                                    {job.bed_included ? "Bed ducked to -12 dB under voice" : "Voice-only (no bed found in imaging project)"}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setPublishOpen(true)}
                                      className="inline-flex items-center gap-1 rounded-md border border-brand-amber/50 bg-bg px-2 py-1 text-[10px] font-semibold text-brand-amber hover:bg-brand-amber/10"
                                      data-testid={`commercial-publish-open-${v.video_id}`}
                                    >
                                      <Share2 className="h-3 w-3" /> Publish to socials
                                    </button>
                                    <a
                                      href={job.ready_url}
                                      download
                                      className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-2 py-1 text-[10px] font-semibold text-black amber-glow"
                                      data-testid={`commercial-fused-download-${v.video_id}`}
                                    >
                                      <Play className="h-3 w-3" /> Save TV MP4
                                    </a>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <CommercialPublishModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        commercial={commercial}
        chosenScriptText={chosenScriptText}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const cls =
    status === "complete" ? "text-emerald-300" :
    status === "in_progress" ? "text-brand-blue" :
    status === "failed" ? "text-brand-onair" :
    "text-brand-amber";
  const label =
    status === "complete" ? "Ready" :
    status === "in_progress" ? "Rendering" :
    status === "failed" ? "Failed" :
    status === "cancelled" ? "Cancelled" :
    "Queued";
  return <span className={cls}>{label}</span>;
}
